import { cloneDeep } from 'lodash';
import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useMutation } from '@apollo/client';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import {
  IProposalMarineDetails,
  IProposalPageFormState,
} from '../../page/generic/form';
import { validateMarineDetailsField } from '../../page/generic/validation';
import { IProposalDetailsSummary } from '../../page/generic/index2';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import { calculateBasicRate } from '../../page/generic/queries';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import PrecisionService from '@services/PrecisionService';
import { MAIN_THEME } from '@styles';
import { useAppSelector } from '@redux/hooks';

interface IProposalMarineDetailsWidgetProps {
  pageState: IProposalPageFormState;
  onPageStateUpdate: (pageState: IProposalPageFormState) => void;
  disabledForm?: boolean;
  lovs: Record<string, Record<string, string>>;
  data: IProposalDetailsSummary;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
  },
  section: {
    padding: '23px 0 25px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 23.7%)',
    gap: '10px 1.75%',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  fullFieldRow: {
    gridColumn: '1 / -1',
    width: '100%',
  },
  halfFieldRow: {
    gridColumn: 'span 2',
    width: '430px',
  },
  labelHeader: {
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
}));

const ProposalMarineDetailsWidget: React.FC<
  IProposalMarineDetailsWidgetProps
> = ({ pageState, onPageStateUpdate, disabledForm, data, lovs }) => {
  const { classes } = useStyles();
  const values = pageState.values.marineDetails;
  const errors = pageState.errors.marineDetails;
  const touched = pageState.touched.marineDetails;
  const isDisabled =
    disabledForm || status === 'CLOSED_WON' || status === 'CLOSED_LOST';

  const { currency } = useAppSelector((state) => state.lovs);
  const currencySymbols = useMemo(() => {
    return lookupListAsRecordObject(currency, false, 'Code', 'Symbol');
  }, [currency]);

  const [calculateBasicRateAction] = useMutation(calculateBasicRate());

  const onFieldUpdate = (
    fieldName: keyof IProposalMarineDetails,
    value: IProposalMarineDetails[keyof IProposalMarineDetails],
    touched = false
  ) => {
    const newPageState = cloneDeep(pageState);
    (newPageState.values.marineDetails as any)[fieldName] = value;
    newPageState.errors.marineDetails[fieldName] = validateMarineDetailsField(
      fieldName,
      value
    );

    if (touched) {
      newPageState.touched.marineDetails[fieldName] = true;
    }
    onPageStateUpdate(newPageState);
  };

  const onFieldBlur = async (fieldName: string) => {
    const newPageState = cloneDeep(pageState);
    newPageState.touched.marineDetails[fieldName] = true;

    if (
      fieldName === 'sumPerShipmentCurrency' ||
      fieldName === 'sumPerShipment'
    ) {
      if (!values.sumPerShipmentCurrency) {
        newPageState.values.marineDetails.sumPerShipmentRate = 0;
        newPageState.values.marineDetails.sumPerShipmentInPolicyCurrency = 0;
        onPageStateUpdate(newPageState);
        return;
      }

      if (values.sumPerShipmentCurrency === data?.PolicyCurrency?.Code) {
        newPageState.values.marineDetails.sumPerShipmentRate = 1;
        newPageState.values.marineDetails.sumPerShipmentInPolicyCurrency =
          PrecisionService.round(values.sumPerShipment);
        onPageStateUpdate(newPageState);
        return;
      }

      try {
        const { data: sumPerShipmentData } = await calculateBasicRateAction({
          variables: { currency: values.sumPerShipmentCurrency },
        });
        const { data: policyData } = await calculateBasicRateAction({
          variables: { currency: data.PolicyCurrency.Code },
        });

        const sumPerShipmentBasicRate =
          sumPerShipmentData?.production?.actions?.calculateBasicRate
            ?.BasicRate;
        const policyToBasicRate =
          policyData?.production?.actions?.calculateBasicRate?.BasicRate;

        const sumPerShipmentRate = sumPerShipmentBasicRate / policyToBasicRate;
        const sumInPolicy = PrecisionService.round(
          values.sumPerShipment * sumPerShipmentRate
        );

        newPageState.values.marineDetails.sumPerShipmentRate =
          sumPerShipmentRate;
        newPageState.values.marineDetails.sumPerShipmentInPolicyCurrency =
          sumInPolicy;
      } catch (err) {
        newPageState.values.marineDetails.sumPerShipmentRate = 0;
        newPageState.values.marineDetails.sumPerShipmentInPolicyCurrency = 0;

        toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
      }
    }

    onPageStateUpdate(newPageState);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Marine Details" isFieldsGrid>
        <SelectFormField
          key="matter"
          name="matter"
          title="Matter*"
          placeholder="Matter"
          value={values.matter}
          error={touched.matter ? errors.matter : ''}
          onChange={(v) => onFieldUpdate('matter', v, true)}
          disabled={
            ['CLOSED_WON, CLOSED_LOST'].includes(data.Status) || disabledForm
          }
          selectOptions={lovs.matters}
          material
          fullWidth
        />

        <TextInputFormField
          key="description"
          name="description"
          title="Description*"
          placeholder="Description"
          value={values.description}
          error={touched.description ? errors.description : ''}
          onBlur={() => onFieldBlur('description')}
          onChange={(v) => onFieldUpdate('description', v.target.value)}
          disabled={disabledForm}
          material
          fullWidth
        />

        <TextInputFormField
          key="vesselName"
          name="vesselName"
          title="Vessel Name*"
          placeholder="Vessel Name"
          value={values.vesselName}
          error={touched.vesselName ? errors.vesselName : ''}
          onBlur={() => onFieldBlur('vesselName')}
          onChange={(v) => onFieldUpdate('vesselName', v.target.value)}
          disabled={disabledForm}
          material
        />

        <CurrencyFormField
          key="vesselAge"
          name="vesselAge"
          title="Vessel Age"
          placeholder="Vessel Age"
          value={values.vesselAge}
          error={touched.vesselAge ? errors.vesselAge : ''}
          onBlur={() => onFieldBlur('vesselAge')}
          onChange={(v) => onFieldUpdate('vesselAge', v.target.value)}
          disabled={disabledForm}
          material
          maxDecimalPrecision={0}
        />

        <TextInputFormField
          key="from"
          name="from"
          title="Departure From*"
          placeholder="Departure From"
          value={values.from}
          error={touched.from ? errors.from : ''}
          onBlur={() => onFieldBlur('from')}
          onChange={(v) => onFieldUpdate('from', v.target.value)}
          disabled={disabledForm}
          material
        />

        <TextInputFormField
          key="to"
          name="to"
          title="Destination To*"
          placeholder="Destination To"
          value={values.to}
          error={touched.to ? errors.to : ''}
          onBlur={() => onFieldBlur('to')}
          onChange={(v) => onFieldUpdate('to', v.target.value)}
          disabled={disabledForm}
          material
        />

        <TextInputFormField
          key="via"
          name="via"
          title="Transportation Via*"
          placeholder="Transportation Via"
          value={values.via}
          error={touched.via ? errors.via : ''}
          onBlur={() => onFieldBlur('via')}
          onChange={(v) => onFieldUpdate('via', v.target.value)}
          disabled={disabledForm}
          material
        />

        <TextInputFormField
          key="survey"
          name="survey"
          title="Survey*"
          placeholder="Survey"
          value={values.survey}
          error={touched.survey ? errors.survey : ''}
          onBlur={() => onFieldBlur('survey')}
          onChange={(v) => onFieldUpdate('survey', v.target.value)}
          disabled={disabledForm}
          material
        />

        <TextInputFormField
          key="cargoContract"
          name="cargoContract"
          title="Cargo Contract*"
          placeholder="Cargo Contract"
          value={values.cargoContract}
          error={touched.cargoContract ? errors.cargoContract : ''}
          onBlur={() => onFieldBlur('cargoContract')}
          onChange={(v) => onFieldUpdate('cargoContract', v.target.value)}
          disabled={disabledForm}
          material
        />

        <SelectFormField
          key="sumPerShipmentCurrency"
          name="sumPerShipmentCurrency"
          title="Sum Per Shipment Currency*"
          placeholder="Sum Per Shipment Currency"
          value={values.sumPerShipmentCurrency}
          error={
            touched.sumPerShipmentCurrency ? errors.sumPerShipmentCurrency : ''
          }
          onBlur={() => onFieldBlur('sumPerShipmentCurrency')}
          onChange={(v) => onFieldUpdate('sumPerShipmentCurrency', v, true)}
          disabled={isDisabled}
          material
          injectGlobalLov={{
            type: 'currency',
            value: 'Code',
            display: 'Title',
          }}
        />

        <CurrencyFormField
          key="sumPerShipment"
          name="sumPerShipment"
          title="Sum Per Shipment"
          placeholder="Sum Per Shipment"
          value={values.sumPerShipment}
          error={touched.sumPerShipment ? errors.sumPerShipment : ''}
          onBlur={() => onFieldBlur('sumPerShipment')}
          onChange={(v) => onFieldUpdate('sumPerShipment', v.target.value)}
          disabled={disabledForm}
          material
          currencySymbol={currencySymbols[values.sumPerShipmentCurrency]}
        />

        <CurrencyFormField
          key="sumPerShipmentInPolicyCurrency"
          name="sumPerShipmentInPolicyCurrency"
          title="Sum Per Shipment In Policy Currency"
          placeholder="Sum Per Shipment In Policy Currency"
          value={values.sumPerShipmentInPolicyCurrency}
          error={
            touched.sumPerShipmentInPolicyCurrency
              ? errors.sumPerShipmentInPolicyCurrency
              : ''
          }
          onBlur={() => onFieldBlur('sumPerShipmentInPolicyCurrency')}
          onChange={() => null}
          disabled
          material
          className={classes.halfFieldRow}
          currencySymbol={data?.PolicyCurrency?.Symbol}
        />

        <TextInputFormField
          key="lcNumber"
          name="lcNumber"
          title="LC Number"
          placeholder="LC Number"
          value={values.lcNumber}
          error={touched.lcNumber ? errors.lcNumber : ''}
          onBlur={() => onFieldBlur('lcNumber')}
          onChange={(v) => onFieldUpdate('lcNumber', v.target.value)}
          disabled={disabledForm}
          material
        />

        <DatePickerFormField
          key="lcIssueDate"
          name="lcIssueDate"
          title="LC Issue Date"
          placeholder="LC Issue Date"
          value={values.lcIssueDate}
          error={touched.lcIssueDate ? errors.lcIssueDate : ''}
          onDateChange={(v) => {
            values.lcIssueDate = v.toLocaleString();
            onFieldUpdate('lcIssueDate', v.toLocaleString(), true);
          }}
          onBlur={() => undefined}
          disabled={disabledForm}
          material
        />

        <TextInputFormField
          key="bank"
          name="bank"
          title="Bank"
          placeholder="Bank"
          value={values.bank}
          error={touched.bank ? errors.bank : ''}
          onBlur={() => onFieldBlur('bank')}
          onChange={(v) => onFieldUpdate('bank', v.target.value)}
          disabled={disabledForm}
          material
        />

        <TextInputFormField
          key="claimsPayableBy"
          name="claimsPayableBy"
          title="Claims Payable By*"
          placeholder="Claims Payable By"
          value={values.claimsPayableBy}
          error={touched.claimsPayableBy ? errors.claimsPayableBy : ''}
          onBlur={() => onFieldBlur('claimsPayableBy')}
          onChange={(v) => onFieldUpdate('claimsPayableBy', v.target.value)}
          disabled={disabledForm}
          material
        />

        <PercentageFormField
          key="loading"
          name="loading"
          title="Loading*"
          placeholder="Loading"
          value={values.loading}
          error={touched.loading ? errors.loading : ''}
          onBlur={() => onFieldBlur('loading')}
          onChange={(v) => onFieldUpdate('loading', v.target.value)}
          disabled={disabledForm}
          material
        />

        <TextInputFormField
          key="remarks"
          name="remarks"
          title="Remarks"
          placeholder="Remarks"
          value={values.remarks}
          error={touched.remarks ? errors.remarks : ''}
          onBlur={() => onFieldBlur('remarks')}
          onChange={(v) => onFieldUpdate('remarks', v.target.value)}
          disabled={disabledForm}
          material
          fullWidth
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export default ProposalMarineDetailsWidget;
